import { useQuery } from '@tanstack/react-query'
import type { Link, ReferAs, Relation } from '../../shared/tree'
import { api, ApiError } from './api'
import type { MomentSummary } from './moments'
import { refUrl, setCrop, type PreparedPhoto } from './photos'

export type TreePerson = {
  id: string
  name: string
  referAs: ReferAs | null
  living: boolean
  born: string | null
  died: string | null
  portrait: string | null
  pet: boolean
  linked: boolean
}
export type Tree = { me: string | null; people: TreePerson[]; links: Link[] }

export type PersonFields = {
  name: string
  referAs: ReferAs | null
  living: boolean
  born: string | null
  died: string | null
  note: string
}
// A memorial page's own words, written by whoever keeps it. Empty until written.
export type MemorialFields = { epitaph: string; story: string; restingPlace: string }
export type Remembrance = {
  id: string
  body: string
  createdAt: number
  author: { id: string; name: string; portrait: string | null }
  mine: boolean
}
export type Person = PersonFields &
  MemorialFields & {
    id: string
    familyId: string
    portrait: string | null
    pet: boolean
    keptBy: { id: string; name: string }
    linked: boolean
    self: boolean
    canEdit: boolean
    canClaim: boolean
    asked: boolean
    connections: {
      relation: Relation
      link: string | null
      removable: boolean
      person: {
        id: string
        name: string
        referAs: ReferAs | null
        living: boolean
        pet: boolean
        portrait: string | null
      }
    }[]
    // book is null for a recipe in no book.
    recipes: { id: string; title: string; book: string | null; cover: string | null }[]
    // Photos they're tagged in, newest first (the first few of photoCount).
    photos: { id: string; ref: string; width: number; height: number; caption: string; takenOn: string | null }[]
    photoCount: number
    // When you're on the tree too: the photos you're both tagged in, newest first.
    together: { id: string; ref: string; width: number; height: number; caption: string; takenOn: string | null }[]
    // The moments that tag them that you wrote or are tagged in too (on your own page, the ones that tag you), most
    // recent first by when they happened: the first few of momentCount.
    moments: MomentSummary[]
    momentCount: number
    // For someone who has passed: what the family loved most about them, oldest first.
    remembrances: Remembrance[]
    requests: { id: string; name: string; email: string }[]
  }

export const useTree = (familyId: string) =>
  useQuery({ queryKey: ['tree', familyId], queryFn: () => api<Tree>(`/families/${encodeURIComponent(familyId)}/tree`) })

export const usePerson = (id: string, options: { enabled?: boolean } = {}) =>
  useQuery({
    queryKey: ['person', id],
    queryFn: () => api<Person>(`/people/${encodeURIComponent(id)}`),
    enabled: options.enabled ?? true,
  })

export const isFace = (ref: string) => ref.startsWith('face:')

// A face is a tree portrait, or a profile photo on someone's account, whose ref starts "face:".
export const portraitUrl = (ref: string, variant: 'original' | 'display' | 'thumb') =>
  isFace(ref) ? refUrl('faces', ref.slice(5), variant) : refUrl('portraits', ref, variant)

export const uploadPortrait = (personId: string, photo: PreparedPhoto) =>
  uploadFace(`/api/people/${personId}/portraits`, photo)
export const uploadProfilePhoto = (photo: PreparedPhoto) => uploadFace('/api/me/photo', photo)

async function uploadFace(path: string, photo: PreparedPhoto) {
  const form = new FormData()
  form.set('original', photo.original)
  form.set('display', photo.display, 'display')
  form.set('thumb', photo.thumb, 'thumb')
  form.set('width', String(photo.width))
  form.set('height', String(photo.height))
  setCrop(form, photo)
  const res = await fetch(path, {
    method: 'POST',
    body: form,
    credentials: 'same-origin',
  })
  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as { error?: string }
    throw new ApiError(res.status, data.error ?? 'The photo didn’t upload. Please try again.')
  }
  return (await res.json()) as { id?: string; ref?: string }
}

export const firstName = (name: string) => name.trim().split(/\s+/)[0] ?? name
